/* Store */

import Alpine from "alpinejs";

declare module "alpinejs" {
  interface Alpine {
    store(name: "cart"): typeof CART_STORE;
  }
}

const _CART_STORE_IMPLEMENTATION = {
  fromPayload(payload: Cart) {
    // Quirk Transforms
    if (payload.cart_level_discount_applications?.[0]?.discount_application) {
      payload.cart_level_discount_applications.forEach((discount) => {
        Object.assign(discount, discount.discount_application);
      });
    }

    Object.assign(this, payload);
  },
  async fetch() {
    await fetch(window.Shopify.routes.root + "cart.js")
      .then((response) => response.json())
      .then((cart) => this.fromPayload(cart));
  },
};

const CART_STORE = _CART_STORE_IMPLEMENTATION as Cart & typeof _CART_STORE_IMPLEMENTATION;

export function initCartStore() {
  Alpine.store("cart", CART_STORE);
}

async function storefrontFetch(query: string, variables: Record<string, any> = {}) {
  const token = window.Shopify?.storefrontAccessToken;
  if (!token) throw new Error("Shopify Storefront Access Token not found");

  const response = await fetch(window.Shopify.routes.root + "api/2023-10/graphql.json", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": token,
    },
    body: JSON.stringify({ query, variables }),
  });

  return response.json();
}

const PRODUCT_SELLING_PLANS_QUERY = `#graphql
  query ProductSellingPlansByHandle(
    $handle: String!
    $sellingPlansFirst: Int = 10
  ) {
    product(handle: $handle) {
      sellingPlanGroups(first: 1, reverse: true) {
        edges {
          node {
            sellingPlans(first: $sellingPlansFirst) {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

export async function getProductSellingPlansByHandle(
  handle: string
): Promise<{ id: number; name: string }[]> {
  const { data } = await storefrontFetch(PRODUCT_SELLING_PLANS_QUERY, { handle });

  return (data?.product?.sellingPlanGroups?.edges ?? []).flatMap((groupEdge: any) =>
    (groupEdge.node.sellingPlans?.edges ?? []).map((planEdge: any) => ({
      id: parseInt(planEdge.node.id.split("/").pop(), 10),
      name: planEdge.node.name,
    }))
  );
}

/* Methods */

type ItemAddPayload = {
  id: string;
  quantity: number;
  selling_plan?: string;
  properties?: Record<string, any>;
};

export async function addToCart({ items }: { items: ItemAddPayload[] }) {
  await fetch(window.Shopify.routes.root + "cart/add.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });

  const payload = await window.Alpine.store("cart").fetch();

  window.dispatchEvent(new CustomEvent("cranky:cart:update", { detail: payload }));
}

export async function updateCartItemQuantity({ id, quantity }: { id: string; quantity: number }) {
  const response = await fetch(window.Shopify.routes.root + "cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, quantity }),
  });

  const payload = (await response.json()) as Cart;

  window.Alpine.store("cart").fromPayload(payload);

  window.dispatchEvent(new CustomEvent("cranky:cart:update", { detail: payload }));
}

export async function updateCartItemSellingPlan({
  id,
  quantity,
  selling_plan,
}: {
  id: string;
  quantity: number;
  selling_plan: string;
}) {
  const response = await fetch(window.Shopify.routes.root + "cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id, quantity, selling_plan }),
  });

  const payload = await response.json();

  if (!response.ok) {
    throw new Error(payload.description || payload.message || "Failed to update selling plan");
  }

  const cartPayload = await window.Alpine.store("cart").fetch();
  window.dispatchEvent(new CustomEvent("cranky:cart:update", { detail: cartPayload }));
}

export async function addBundleToCart({
  type,
  bundleId,
  bundleSku,
  bundleTitle,
  bundledItems,
  selling_plan,
}: {
  type: "real" | "virtual";
  bundleId: string;
  bundleSku: string;
  bundleTitle: string;
  bundledItems: (Omit<ItemAddPayload, "selling_plan"> & { title: string; compare_at_price: number })[];
  selling_plan?: string;
}) {
  const groupId = crypto.randomUUID();
  const items =
    type === "real"
      ? [
          {
            id: bundleId,
            quantity: 1,
            selling_plan,
            properties: { _components: JSON.stringify(bundledItems) },
          },
        ]
      : bundledItems.map((item) => ({
          ...item,
          selling_plan,
          properties: {
            _group_id: groupId,
            _compare_at_price: item.compare_at_price,
            _bundle_type: type,
            _bundle_id: bundleId,
            _bundle_name: bundleTitle,
            _bundle_sku: bundleSku,
            Bundle: bundleTitle,
          },
        }));

  await addToCart({ items });
}

/* Types */

interface Cart {
  token: string;
  note: string;
  attributes: Record<string, any>;
  original_total_price: number;
  total_price: number;
  total_discount: number;
  total_weight: number;
  item_count: number;
  items: CartItem[];
  requires_shipping: boolean;
  currency: string;
  items_subtotal_price: number;
  cart_level_discount_applications: DiscountApplication[];
  items_added: ItemChange[];
  items_removed: ItemChange[];
}

interface CartItem {
  id: number;
  properties: Record<string, any>;
  quantity: number;
  variant_id: number;
  key: string;
  title: string;
  product_title: string;
  variant_title: string | null;
  price: number;
  original_price: number;
  discounted_price: number;
  line_price: number;
  original_line_price: number;
  presentment_price: number;
  total_discount: number;
  discounts: Discount[];
  sku: string;
  grams: number;
  vendor: string;
  taxable: boolean;
  product_id: number;
  product_has_only_default_variant: boolean;
  gift_card: boolean;
  final_price: number;
  final_line_price: number;
  url: string;
  featured_image: Image;
  image: string;
  handle: string;
  requires_shipping: boolean;
  product_type: string;
  untranslated_product_title: string;
  product_description: string;
  untranslated_variant_title: string | null;
  variant_options: string[];
  options_with_values: OptionWithValue[];
  line_level_discount_allocations: any[]; // Adjust this type if you have more information
  line_level_total_discount: number;
  selling_plan_allocation?: SellingPlanAllocation;
}

interface Discount {
  amount: number;
  title: string;
}

interface Image {
  alt: string;
  aspect_ratio: number;
  height: number;
  url: string;
  width: number;
}

interface OptionWithValue {
  name: string;
  value: string;
}

interface SellingPlanAllocation {
  price_adjustments: PriceAdjustment[];
  price: number;
  compare_at_price: number;
  per_delivery_price: number;
  selling_plan: SellingPlan;
}

interface PriceAdjustment {
  position: number;
  price: number;
}

interface SellingPlan {
  id: number;
  name: string;
  description: string | null;
  options: SellingPlanOption[];
  recurring_deliveries: boolean;
  price_adjustments: PriceAdjustmentDetail[];
  checkout_charge: CheckoutCharge;
}

interface SellingPlanOption {
  name: string;
  position: number;
  value: string;
}

interface PriceAdjustmentDetail {
  order_count: number | null;
  position: number;
  value_type: string;
  value: number;
}

interface CheckoutCharge {
  value_type: string;
  value: number;
}

interface DiscountApplication {
  amount: number;
  discount_application: DiscountApplicationDetail;
}

interface DiscountApplicationDetail {
  allocation_method: string;
  created_at: string;
  description: string | null;
  key: string;
  target_selection: string;
  target_type: string;
  title: string;
  total_allocated_amount: number;
  type: string;
  value: string;
  value_type: string;
}

interface ItemChange {
  product_id: number;
  variant_id: number;
  id: string;
  image: string;
  price: string;
  presentment_price: number;
  quantity: number;
  title: string;
  product_title: string;
  variant_title: string;
  vendor: string;
  product_type: string;
  sku: string;
  url: string;
  untranslated_product_title: string;
  untranslated_variant_title: string;
  view_key: string;
}
